import { useCallback, useContext } from "react";

import { useParams } from "react-router";
import { useUnmount } from "react-use";

import useFAST from "../contexts/FASTContext";
import { WebSocketContext } from "../contexts/WebSocket";

import type { FASTChannelRoutesParams } from "../components/routes/FASTChannelRoutes";

interface GetCanUpdateSchedule {
  onSuccess: () => void;
  onError: () => void;
}
interface UseFASTScheduleWebSocketReturn {
  lockSchedule: () => void;
  releaseSchedule: () => void;
  getCanUpdateSchedule: (params: GetCanUpdateSchedule) => void;
  confirmUpdatedSchedule: (startDate: string) => void;
}

export default function useFASTScheduleWebSocket(): UseFASTScheduleWebSocketReturn {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { dispatch } = useFAST();

  const channelTopic = `fast_schedule:${channelId}`;

  const { pushMessage } = useContext(WebSocketContext);

  const lockSchedule = useCallback((): void => {
    pushMessage({
      channelTopic,
      event: "lock_schedule",
      onSuccess: () => {
        dispatch({ type: "LOCK_SCHEDULE", isLocked: true, isOwned: true });
      },
      onError: () => {
        console.error("Error locking schedule");
      },
    });
  }, [pushMessage, channelTopic, dispatch]);

  const getCanUpdateSchedule = useCallback(
    ({ onSuccess, onError }: GetCanUpdateSchedule) => {
      pushMessage<boolean>({
        channelTopic,
        event: "can_update_schedule",
        onSuccess: (canUpdate) => (canUpdate ? onSuccess() : onError()),
        onError,
      });
    },
    [pushMessage, channelTopic],
  );

  const confirmUpdatedSchedule = useCallback(
    (startDate: string) => {
      pushMessage<{
        error: string;
      }>({
        channelTopic,
        event: "schedule_updated",
        payload: { start_date: startDate },
      });
    },
    [pushMessage, channelTopic],
  );

  const releaseSchedule = useCallback((): void => {
    pushMessage({
      channelTopic,
      event: "release_schedule",
      onSuccess: () => dispatch({ type: "RELEASE_SCHEDULE" }),
    });
  }, [pushMessage, channelTopic, dispatch]);

  useUnmount(() => {
    releaseSchedule();
  });

  return {
    getCanUpdateSchedule,
    confirmUpdatedSchedule,
    lockSchedule,
    releaseSchedule,
  };
}
